import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Sparkles, Loader2, Check } from 'lucide-react';

/**
 * Shows an AI-suggested name for an image and lets the user accept it or keep/edit
 * their own. Controlled via `prompt` ({ file, originalName, suggestion, loading }).
 * onConfirm(finalName | null) — null means "keep original filename".
 */
export const AiImageNameDialog = ({ prompt, onConfirm, onSkip }) => {
  const open = !!prompt;
  const [name, setName] = useState('');

  useEffect(() => {
    if (prompt) setName(prompt.suggestion || prompt.originalBase || '');
  }, [prompt]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onSkip?.(); }}>
      <DialogContent className="max-w-md" data-testid="ai-name-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-500" /> Name this photo
          </DialogTitle>
          <DialogDescription>
            {prompt.loading ? 'Asking AI for a suggested name…' : `For ${prompt.originalName}`}
          </DialogDescription>
        </DialogHeader>

        {prompt.previewUrl && (
          <img src={prompt.previewUrl} alt="preview" className="max-h-40 w-auto mx-auto rounded-md border" />
        )}

        {prompt.loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-4">
            {prompt.suggestion ? (
              <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2">
                <p className="text-xs text-violet-600 font-medium flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> AI suggestion
                </p>
                <p className="text-sm font-semibold text-slate-800" data-testid="ai-suggested-name">{prompt.suggestion}</p>
              </div>
            ) : (
              <p className="text-xs text-slate-400">AI couldn't suggest a name — edit below or keep the original.</p>
            )}

            <div>
              <label className="text-xs text-slate-500">Document name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="ai-name-input" autoFocus />
            </div>

            <div className="flex flex-col gap-2">
              {prompt.suggestion && (
                <Button
                  onClick={() => onConfirm(prompt.suggestion)}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="ai-use-name-btn"
                >
                  <Check className="w-4 h-4 mr-2" /> Use AI name "{prompt.suggestion}"
                </Button>
              )}
              <Button variant="outline" onClick={() => onConfirm(name.trim() ? name.trim() : null)} className="w-full" data-testid="ai-keep-name-btn">
                {name.trim() && name.trim() !== prompt.suggestion ? 'Save with my name' : 'Keep original name'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
